import React from 'react';

interface CardProps {
  variant?: 'solid' | 'glass' | 'gradient' | 'outline';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
}

export const Card: React.FC<CardProps> = ({
  variant = 'solid',
  padding = 'md',
  hover = false,
  className = '',
  children,
  onClick,
}) => {
  const baseStyles = 'rounded-xl transition-all';

  const variants = {
    solid: `
      bg-surface border border-theme-light shadow-theme-sm
      dark:border-border
    `,
    glass: `
      glass-card shadow-glass
    `,
    gradient: `
      gradient-primary text-white shadow-lg
    `,
    outline: `
      bg-transparent border border-theme
      dark:border-border
    `,
  };

  const paddings = {
    none: '',
    sm: 'p-3',
    md: 'p-4 md:p-5',
    lg: 'p-6 md:p-8',
  };

  const hoverStyles = hover
    ? 'cursor-pointer hover:shadow-theme-md hover:border-primary-200 dark:hover:border-primary-700 hover:-translate-y-0.5'
    : '';

  return (
    <div
      className={`
        ${baseStyles}
        ${variants[variant]}
        ${paddings[padding]}
        ${hoverStyles}
        ${className}
      `}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {children}
    </div>
  );
};

// Card Header component
interface CardHeaderProps {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  action,
  icon,
  className = '',
}) => {
  return (
    <div className={`flex items-start justify-between mb-4 ${className}`}>
      <div className="flex items-center gap-3">
        {icon && (
          <div className="p-2 rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
            {icon}
          </div>
        )}
        <div>
          <h3 className="font-semibold text-theme">{title}</h3>
          {subtitle && (
            <p className="text-sm text-theme-secondary mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
};

// Gradient Card component (like mobile's GradientCard)
interface GradientCardProps {
  children: React.ReactNode;
  className?: string;
}

export const GradientCard: React.FC<GradientCardProps> = ({
  children,
  className = '',
}) => {
  return (
    <div
      className={`
        gradient-primary rounded-2xl p-6 text-white shadow-lg
        ${className}
      `}
    >
      {children}
    </div>
  );
};

// Stats Card component
interface StatsCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  variant?: 'default' | 'glass' | 'gradient';
  className?: string;
}

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  icon,
  trend,
  variant = 'default',
  className = '',
}) => {
  const isGradient = variant === 'gradient';

  return (
    <Card variant={variant === 'glass' ? 'glass' : variant === 'gradient' ? 'gradient' : 'solid'} className={className}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-sm font-medium ${isGradient ? 'text-white/80' : 'text-theme-secondary'}`}>
            {title}
          </p>
          <p className={`text-2xl font-bold mt-1 ${isGradient ? 'text-white' : 'text-theme'}`}>
            {value}
          </p>
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${
              trend.isPositive
                ? isGradient ? 'text-green-200' : 'text-green-600 dark:text-green-400'
                : isGradient ? 'text-red-200' : 'text-red-600 dark:text-red-400'
            }`}>
              <span>{trend.isPositive ? '↑' : '↓'}</span>
              <span>{Math.abs(trend.value)}%</span>
            </div>
          )}
        </div>
        {icon && (
          <div className={`p-3 rounded-xl ${
            isGradient
              ? 'bg-white/20'
              : 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400'
          }`}>
            {icon}
          </div>
        )}
      </div>
    </Card>
  );
};
